/**
 * Find the page's menu / header / footer block when it isn't marked up as a
 * landmark. Shared by the landmarks rule (report a missing landmark only when
 * such a block exists — a page with no menu needs no nav) and the widget's
 * runtime fix (give exactly that block the role), so a report never claims a
 * fix the widget can't make.
 */

export const FOOTERISH = 'footer, [role="contentinfo"], [class*="footer" i], [id*="footer" i]';
const HEADERISH = 'header, [role="banner"], [class*="header" i], [id*="header" i]';
const OUTSIDE_CONTENT = 'main, [role="main"], article';

function shown(el: Element): boolean {
  if (el.closest('[hidden], [aria-hidden="true"]')) return false;
  return typeof el.getClientRects !== 'function' || el.getClientRects().length > 0;
}

function rect(el: Element): { top: number; width: number; height: number } {
  const r = typeof el.getBoundingClientRect === 'function' ? el.getBoundingClientRect() : null;
  const sy = typeof window !== 'undefined' ? window.scrollY || 0 : 0;
  return { top: (r?.top ?? 0) + sy, width: r?.width ?? 0, height: r?.height ?? 0 };
}

function linkShare(el: Element): { links: number; share: number } {
  const links = el.querySelectorAll('a[href]');
  let linkText = 0;
  links.forEach((a) => { linkText += (a.textContent || '').trim().length; });
  const all = (el.textContent || '').trim().length || 1;
  return { links: links.length, share: linkText / all };
}

/** The main menu: a list of 3–40 links that is mostly links, outside the
 *  footer and the content, preferring one in a header and near the top. */
export function findMenu(doc: Document): HTMLElement | null {
  let best: { el: HTMLElement; score: number } | null = null;
  const lists = Array.from(doc.body?.querySelectorAll<HTMLElement>('ul, ol') || []).slice(0, 1500);
  for (const el of lists) {
    if (!shown(el) || el.closest(FOOTERISH) || el.closest(OUTSIDE_CONTENT)) continue;
    if (el.parentElement?.closest('ul, ol')) continue; // a submenu — its top list is the candidate
    const { links, share } = linkShare(el);
    if (links < 3 || links > 40 || share < 0.6) continue;
    const score = (el.closest(HEADERISH) ? 1000 : 0) - rect(el).top / 10 + links;
    if (!best || score > best.score) best = { el, score };
  }
  return best?.el ?? null;
}

/** The page header: a wide block at the very top holding the menu or home link. */
export function findBanner(doc: Document): HTMLElement | null {
  const vw = doc.documentElement.clientWidth || (typeof window !== 'undefined' ? window.innerWidth : 0);
  const fits = (el: HTMLElement): boolean => {
    const r = rect(el);
    // Never a <nav>: role=banner would replace its navigation role. When the
    // whole top bar IS the nav, the page has its landmark and needs no banner.
    return r.top < 160 && r.width >= vw * 0.6 && r.height > 20 && r.height < 420
      && !el.matches('nav, [role="navigation"]') && !el.closest('nav, [role="navigation"]')
      && !el.closest(OUTSIDE_CONTENT) && !el.querySelector('main, [role="main"]');
  };
  const named = Array.from(doc.body?.querySelectorAll<HTMLElement>('[class*="header" i], [id*="header" i]') || [])
    .find((el) => shown(el) && !el.parentElement?.closest('[class*="header" i], [id*="header" i]') && fits(el));
  if (named) return named;
  let cur: HTMLElement | null = findMenu(doc) || doc.querySelector<HTMLElement>('a[href="/"]');
  let pick: HTMLElement | null = null;
  while (cur && cur !== doc.body) {
    if (fits(cur)) pick = cur;
    cur = cur.parentElement;
  }
  return pick;
}

/** The page footer: the outermost footer-named block in the lower half. */
export function findFooter(doc: Document): HTMLElement | null {
  const half = (doc.documentElement.scrollHeight || 0) / 2;
  const named = Array.from(doc.body?.querySelectorAll<HTMLElement>('[class*="footer" i], [id*="footer" i]') || [])
    .filter((el) => shown(el) && !el.parentElement?.closest('[class*="footer" i], [id*="footer" i]')
      && !el.closest(OUTSIDE_CONTENT) && rect(el).top >= half);
  return named[named.length - 1] ?? null;
}

/** Page-level header/footer (not one inside an article or section). */
export function hasLandmark(doc: Document, kind: 'nav' | 'banner' | 'contentinfo'): boolean {
  if (kind === 'nav') return !!doc.querySelector('nav, [role="navigation"]');
  if (kind === 'banner') return !!doc.querySelector('[role="banner"]')
    || Array.from(doc.querySelectorAll('header')).some((h) => !h.closest('main, article, section, aside, nav'));
  return !!doc.querySelector('[role="contentinfo"]')
    || Array.from(doc.querySelectorAll('footer')).some((f) => !f.closest('main, article, section, aside, nav'));
}

export function findLandmarkBlock(doc: Document, kind: string): HTMLElement | null {
  if (kind === 'nav') return findMenu(doc);
  if (kind === 'banner') return findBanner(doc);
  if (kind === 'contentinfo') return findFooter(doc);
  return null;
}
